import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { ApiError, request } from '../api/client';

interface Conversa {
  id: string;
  pausada: boolean;
  intervencaoHumana: boolean;
  flagIntervencao: string | null;
  ultimaInstancia: string | null;
}

interface ClienteDetail {
  id: string;
  primeiroNome: string;
  telefone: string;
  status: string;
}

interface Anexo {
  tipo: string;
  mimetype: string;
  base64: string;
  caption: string | null;
}

interface Mensagem {
  id: string;
  autor: 'cliente' | 'sara' | 'humano';
  conteudo: string;
  tags: string[];
  intent: string | null;
  enviadaEm: string;
  anexos: unknown;
}

interface ConversaDetail {
  conversa: Conversa;
  cliente: ClienteDetail;
  mensagens: Mensagem[];
}

interface ChatwootMeta {
  labels: string[];
  assignee: { id: number; name: string; email: string } | null;
}

interface ChatwootAgent {
  id: number;
  name: string;
  email: string;
}

interface InstanceInfo {
  name: string;
  connectedNumber: string | null;
  lastConnectedNumber: string | null;
}

export function ConversaDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery<ConversaDetail>({
    queryKey: ['conversa-detail', id],
    queryFn: () => request<ConversaDetail>(`/admin/conversas/${id}`),
    enabled: !!id,
    refetchInterval: 10_000,
  });

  if (!id) return <div className="card">ID inválido</div>;
  if (isLoading) return <div className="card muted">Carregando…</div>;
  if (isError || !data) {
    return (
      <div className="card border-sara-danger">
        <p className="text-sara-danger">Erro ao carregar.</p>
        <button type="button" className="btn-ghost mt-2" onClick={() => refetch()}>
          Tentar de novo
        </button>
      </div>
    );
  }

  return (
    <section className="space-y-4">
      <header className="flex items-center gap-3">
        <button type="button" className="btn-ghost text-sm" onClick={() => navigate('/conversas')}>
          ← Conversas
        </button>
        <div className="flex-1">
          <h1 className="h1">{data.cliente.primeiroNome || 'amiga'}</h1>
          <p className="text-xs muted">
            {formatPhone(data.cliente.telefone)} ·{' '}
            {data.conversa.ultimaInstancia ?? 'sem instância'} ·{' '}
            {data.conversa.pausada ? '🛑 pausada' : '✓ ativa'}
          </p>
        </div>
      </header>

      <div className="grid lg:grid-cols-[1fr_360px] gap-4 items-start">
        <ChatPanel
          mensagens={data.mensagens}
          telefone={data.cliente.telefone}
          conversaId={data.conversa.id}
          ultimaInstancia={data.conversa.ultimaInstancia}
          onAfterSend={() => {
            void queryClient.invalidateQueries({ queryKey: ['conversa-detail', id] });
          }}
        />
        <ChatwootSidebar
          conversaId={data.conversa.id}
          onChange={() => {
            void queryClient.invalidateQueries({ queryKey: ['conversa-detail', id] });
          }}
        />
      </div>
    </section>
  );
}

function ChatPanel({
  mensagens,
  conversaId,
  ultimaInstancia,
  onAfterSend,
}: {
  mensagens: Mensagem[];
  telefone: string;
  conversaId: string;
  ultimaInstancia: string | null;
  onAfterSend: () => void;
}): JSX.Element {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showSendBar, setShowSendBar] = useState(false);
  const [text, setText] = useState('');
  const [instancia, setInstancia] = useState(ultimaInstancia ?? '');

  useEffect(() => {
    setInstancia(ultimaInstancia ?? '');
  }, [ultimaInstancia]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [mensagens.length]);

  const { data: instances } = useQuery<InstanceInfo[]>({
    queryKey: ['admin-instances'],
    queryFn: () => request<InstanceInfo[]>('/admin/instances'),
    staleTime: 60_000,
  });
  const { data: cloudConfigs } = useQuery<
    Array<{ id: string; nome: string; displayPhoneNumber: string; ativo: boolean }>
  >({
    queryKey: ['cloud-configs'],
    queryFn: () =>
      request<Array<{ id: string; nome: string; displayPhoneNumber: string; ativo: boolean }>>(
        '/admin/whatsapp-cloud/configs',
      ),
    staleTime: 60_000,
  });

  const send = useMutation({
    mutationFn: (body: { instancia?: string; cloudConfigId?: string; texto: string }) =>
      request<{ messageId: string | null; canal: string; via: string }>(
        `/admin/conversas/${conversaId}/send-human`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      setText('');
      setShowSendBar(false);
      onAfterSend();
    },
  });

  function handleSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    const t = text.trim();
    if (!t || !instancia) return;
    if (instancia.startsWith('cloud:')) {
      send.mutate({ cloudConfigId: instancia.slice('cloud:'.length), texto: t });
    } else {
      send.mutate({ instancia, texto: t });
    }
  }

  return (
    <article className="card flex flex-col h-[75vh]">
      <div className="flex items-center justify-between pb-2 border-b border-sara-border">
        <h2 className="h2">Conversa</h2>
        <button
          type="button"
          className="btn-ghost text-xs"
          onClick={() => setShowSendBar((v) => !v)}
        >
          {showSendBar ? 'Fechar resposta manual' : '📞 Responder por outro nº'}
        </button>
      </div>

      <div ref={scrollRef} className="flex-1 overflow-y-auto py-3 space-y-2.5">
        {mensagens.length === 0 ? (
          <p className="text-sara-text-subtle text-sm text-center italic mt-8">
            Sem mensagens ainda.
          </p>
        ) : (
          mensagens.map((m) => <MessageBubble key={m.id} msg={m} />)
        )}
      </div>

      {showSendBar && (
        <form
          onSubmit={handleSubmit}
          className="pt-3 border-t border-sara-border space-y-2"
          aria-label="Enviar resposta manual"
        >
          <div className="flex gap-2 items-center">
            <label htmlFor="instancia-select" className="text-xs muted whitespace-nowrap">
              Por:
            </label>
            <select
              id="instancia-select"
              className="input flex-1"
              value={instancia}
              onChange={(e) => setInstancia(e.target.value)}
            >
              <option value="">Escolha o canal…</option>
              {(instances ?? []).length > 0 && (
                <optgroup label="Evolution">
                  {(instances ?? []).map((i) => (
                    <option key={i.name} value={i.name}>
                      {i.name}{' '}
                      {i.connectedNumber
                        ? `(${i.connectedNumber} ✓)`
                        : i.lastConnectedNumber
                          ? `(${i.lastConnectedNumber} desconectada)`
                          : '(sem nº)'}
                    </option>
                  ))}
                </optgroup>
              )}
              {(cloudConfigs ?? []).filter((c) => c.ativo).length > 0 && (
                <optgroup label="WhatsApp Cloud (Oficial)">
                  {(cloudConfigs ?? [])
                    .filter((c) => c.ativo)
                    .map((c) => (
                      <option key={c.id} value={`cloud:${c.id}`}>
                        {c.nome} ({c.displayPhoneNumber})
                      </option>
                    ))}
                </optgroup>
              )}
            </select>
          </div>
          <div className="flex gap-2">
            <textarea
              className="input flex-1 min-h-[60px] resize-y"
              placeholder="Digita a resposta (vai como humano, atualiza o nº de resposta da Sara)…"
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={send.isPending}
              maxLength={2000}
            />
            <button
              type="submit"
              className="btn-primary self-end"
              disabled={send.isPending || !text.trim() || !instancia}
              aria-busy={send.isPending}
            >
              {send.isPending ? 'Enviando…' : 'Enviar'}
            </button>
          </div>
          {send.isError && (
            <p role="alert" className="text-sara-danger text-xs">
              {send.error instanceof ApiError ? send.error.message : 'Erro ao enviar.'}
            </p>
          )}
          <p className="text-xs text-sara-text-subtle">
            Mensagem entra como <strong>humano</strong> no histórico. Sara passa a responder pela
            instância escolhida nas próximas mensagens.
          </p>
        </form>
      )}
    </article>
  );
}

function MessageBubble({ msg }: { msg: Mensagem }): JSX.Element {
  const isCliente = msg.autor === 'cliente';
  const isHumano = msg.autor === 'humano';
  const align = isCliente ? 'justify-start' : 'justify-end';
  const bg = isCliente
    ? 'bg-sara-bg border border-sara-border'
    : isHumano
      ? 'bg-amber-500/15 border border-amber-500/40 text-sara-text'
      : 'bg-sara-accent text-sara-accent-fg';
  const radius = isCliente ? 'rounded-bl-sm' : 'rounded-br-sm';
  const label = isCliente ? 'Cliente' : isHumano ? 'Humano' : 'Sara';
  const anexos = parseAnexos(msg.anexos);

  return (
    <div className={`flex ${align}`}>
      <div
        className={`max-w-[80%] rounded-2xl px-3.5 py-2 text-sm whitespace-pre-wrap break-words ${bg} ${radius}`}
      >
        <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1 flex justify-between gap-3">
          <span>{label}</span>
          <span>{formatTime(msg.enviadaEm)}</span>
        </div>
        {anexos.map((a, i) => (
          <a
            key={i}
            href={`data:${a.mimetype};base64,${a.base64}`}
            target="_blank"
            rel="noreferrer"
            className="block mb-1"
          >
            <img
              src={`data:${a.mimetype};base64,${a.base64}`}
              alt={a.caption ?? 'imagem enviada pela cliente'}
              className="rounded-lg max-w-full max-h-72 object-contain"
            />
          </a>
        ))}
        {msg.conteudo}
        {msg.intent && !isCliente && (
          <div className="mt-1.5 text-[10px] uppercase tracking-wider opacity-50">
            intent: {msg.intent}
            {msg.tags.length > 0 ? ` · ${msg.tags.join(' · ')}` : ''}
          </div>
        )}
      </div>
    </div>
  );
}

/** Lê `Mensagem.anexos` (Json) defensivamente como lista de imagens base64. */
function parseAnexos(raw: unknown): Anexo[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter(
    (a): a is Anexo =>
      typeof a === 'object' &&
      a !== null &&
      typeof (a as Anexo).mimetype === 'string' &&
      typeof (a as Anexo).base64 === 'string',
  );
}

function ChatwootSidebar({
  conversaId,
  onChange,
}: {
  conversaId: string;
  onChange: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [newLabel, setNewLabel] = useState('');
  const [selectedAgent, setSelectedAgent] = useState<string>('');

  const { data: meta, isLoading: metaLoading } = useQuery<ChatwootMeta>({
    queryKey: ['chatwoot-meta', conversaId],
    queryFn: () => request<ChatwootMeta>(`/admin/conversas/${conversaId}/chatwoot-meta`),
    refetchInterval: 15_000,
  });
  const { data: agents } = useQuery<ChatwootAgent[]>({
    queryKey: ['chatwoot-agents'],
    queryFn: () => request<ChatwootAgent[]>('/admin/chatwoot/agents'),
    staleTime: 60_000,
  });

  const assigneeId = meta?.assignee?.id;
  useEffect(() => {
    if (assigneeId) setSelectedAgent(String(assigneeId));
    else setSelectedAgent('');
  }, [assigneeId]);

  function invalidate(): void {
    void queryClient.invalidateQueries({ queryKey: ['chatwoot-meta', conversaId] });
    onChange();
  }

  const addLabel = useMutation({
    mutationFn: (labels: string[]) =>
      request<{ labels: string[] }>(`/admin/conversas/${conversaId}/chatwoot-labels`, {
        method: 'POST',
        body: { labels },
      }),
    onSuccess: () => {
      setNewLabel('');
      invalidate();
    },
  });

  const removeLabel = useMutation({
    mutationFn: (label: string) =>
      request<{ labels: string[] }>(`/admin/conversas/${conversaId}/chatwoot-labels/remove`, {
        method: 'POST',
        body: { label },
      }),
    onSuccess: invalidate,
  });

  const setAssignee = useMutation({
    mutationFn: (agentId: number | null) =>
      request<{ assigned: boolean; agentId: number | null }>(
        `/admin/conversas/${conversaId}/chatwoot-assignee`,
        { method: 'POST', body: { agentId } },
      ),
    onSuccess: invalidate,
  });

  function handleAddLabel(): void {
    const slug = newLabel.trim().toLowerCase().replace(/\s+/g, '-');
    if (!slug) return;
    const current = meta?.labels ?? [];
    if (current.includes(slug)) {
      setNewLabel('');
      return;
    }
    addLabel.mutate([...current, slug]);
  }

  return (
    <aside className="card space-y-5 sticky top-4">
      <div>
        <h2 className="h2">Chatwoot</h2>
        <p className="text-xs muted mt-0.5">
          Etiquetas + responsável. Aplicado direto no Chatwoot.
        </p>
      </div>

      {metaLoading ? (
        <p className="text-xs muted">Carregando metadata…</p>
      ) : (
        <>
          <section>
            <p className="label">Etiquetas</p>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {(meta?.labels ?? []).length === 0 && (
                <p className="text-xs text-sara-text-subtle italic">Sem etiquetas.</p>
              )}
              {(meta?.labels ?? []).map((l) => (
                <span
                  key={l}
                  className="inline-flex items-center gap-1 rounded-full bg-sara-accent/10 text-sara-accent text-xs px-2 py-1"
                >
                  {l}
                  <button
                    type="button"
                    onClick={() => removeLabel.mutate(l)}
                    className="text-sara-text-muted hover:text-sara-danger leading-none"
                    aria-label={`Remover ${l}`}
                    disabled={removeLabel.isPending}
                  >
                    ×
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className="input flex-1 text-sm"
                placeholder="nova etiqueta (hífen, sem `:`)"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddLabel();
                  }
                }}
              />
              <button
                type="button"
                className="btn-ghost text-xs"
                onClick={handleAddLabel}
                disabled={!newLabel.trim() || addLabel.isPending}
              >
                + Adicionar
              </button>
            </div>
            {(addLabel.isError || removeLabel.isError) && (
              <p role="alert" className="text-sara-danger text-xs mt-1">
                Erro ao atualizar etiqueta.
              </p>
            )}
          </section>

          <hr className="border-sara-border" />

          <section>
            <p className="label">Responsável</p>
            <select
              className="input text-sm"
              value={selectedAgent}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedAgent(v);
                setAssignee.mutate(v === '' ? null : Number(v));
              }}
              disabled={setAssignee.isPending}
            >
              <option value="">— Sem responsável —</option>
              {(agents ?? []).map((a) => (
                <option key={a.id} value={String(a.id)}>
                  {a.name} ({a.email})
                </option>
              ))}
            </select>
            {meta?.assignee && (
              <p className="text-xs text-sara-text-subtle mt-1.5">Atual: {meta.assignee.name}</p>
            )}
            {setAssignee.isError && (
              <p role="alert" className="text-sara-danger text-xs mt-1">
                Erro ao atribuir.
              </p>
            )}
          </section>
        </>
      )}
    </aside>
  );
}

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 13 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 9)}-${digits.slice(9)}`;
  }
  if (digits.length === 12 && digits.startsWith('55')) {
    return `+${digits.slice(0, 2)} (${digits.slice(2, 4)}) ${digits.slice(4, 8)}-${digits.slice(8)}`;
  }
  return digits;
}
